"use client";
import { format } from "date-fns";
import Image from "next/image";
import Link from "next/link";
import React, { useCallback } from "react";

import FilterLink from "@/components/Blog/FilterLink";
import ConditionalWrap from "@/components/ConditionalWrap";
import { excludeBlogHiddenTags } from "@cgc/core/utils/tags";
import { Paragraph } from "@cgc/ui/typography";

type CarouselArticleItemProps = {
  title?: string;
  image?: string;
  summary?: string;
  tags?: string[];
  categories?: string[];
  datePublished?: string;
  link?: string;
};

const stripSummary = (summary: string, limit = 120) =>
  summary.length > limit ? `${summary.slice(0, limit)}...` : summary;

const CarouselArticleItem: React.FC<CarouselArticleItemProps> = ({
  title,
  image,
  summary,
  tags,
  categories,
  datePublished,
  link,
}) => {
  const ImageWrapperComponent = useCallback(
    ({ children }) => {
      const props = {
        className: "relative h-[153px] block",
      };

      return link ? (
        <Link href={link!} {...props}>
          {children}
        </Link>
      ) : (
        <div {...props}>{children}</div>
      );
    },
    [link],
  );

  return (
    <article className="mb-2 flex flex-col">
      {image && (
        <ImageWrapperComponent>
          <Image
            src={image}
            alt={title ?? ""}
            fill
            className="object-cover object-center"
          />
        </ImageWrapperComponent>
      )}
      <div className="border-cgc-yellow-60 bg-cgc-white shadow-blog-carousel relative z-10 mx-3 -mt-[25px] border-t-[6px] px-1.5 pb-2.5 text-center">
        {datePublished && (
          <Paragraph size="p3" className="text-cgc-blue-80 my-1">
            {format(new Date(datePublished), "MMMM d, yyyy")}
          </Paragraph>
        )}
        <ConditionalWrap
          condition={!!link}
          wrap={({ children }) => (
            <Link href={link!} className="mb-2 no-underline decoration-0">
              {children}
            </Link>
          )}
        >
          <Paragraph size="sub3">{title}</Paragraph>
        </ConditionalWrap>
        {summary && (
          <Paragraph size="p2" className="my-2.5">
            {stripSummary(summary)}
          </Paragraph>
        )}
        {tags && (
          <div>
            {excludeBlogHiddenTags(tags).map((tag, index, collection) => (
              <FilterLink
                param="tag"
                value={tag}
                key={`tag:${link}:${tag}:${index}`}
                className="text-cgc-blue mr-1.5 underline"
              >
                <Paragraph size="p2" as="span">
                  {tag}
                  {index !== collection.length - 1 ? "," : ""}
                </Paragraph>
              </FilterLink>
            ))}
          </div>
        )}
        {categories && (
          <div>
            {categories.map((category, index) => (
              <>
                <FilterLink
                  param="category"
                  value={category}
                  key={`category:${link}:${category}:${index}`}
                  className="text-cgc-blue underline"
                >
                  <Paragraph size="p2" as="span">
                    {category}
                    {index !== categories.length - 1 ? "," : ""}
                  </Paragraph>
                </FilterLink>{" "}
              </>
            ))}
          </div>
        )}
      </div>
    </article>
  );
};

export default CarouselArticleItem;
